// ─── In-app attachment preview ───────────────────────────────────────────────
//
// The rules behind the preview panel, kept out of the component so they can be
// unit-tested — vitest only collects `src/**/*.test.ts`, so anything left in the
// .tsx has no coverage at all.
//
// The server is the authority on whether a file can be previewed: it decides
// before signing anything, so a caller cannot talk the API into an inline URL
// for an arbitrary object (`isPreviewableMime` in the backend's
// attachment-config.ts). What lives here is the same list, used only to decide
// whether to OFFER the control — never to grant access.

/** Types the viewer renders itself. Mirrors ATTACHMENT_PREVIEWABLE_MIME. */
export const PREVIEWABLE_MIME: readonly string[] = [
  'image/jpeg',
  'image/png',
  'application/pdf',
]

/**
 * TIFF is deliberately absent, in both lists and in the backend's.
 *
 * No browser decodes it natively, and it cannot be uploaded here either — the
 * accepted-upload list is jpeg/png/pdf — so there is nothing stored to render.
 * Supporting it means allowing the upload AND shipping a decoder; that is a
 * different ticket, not a line in this one.
 */
export function isPreviewableMime(mimeType: string): boolean {
  return PREVIEWABLE_MIME.includes(mimeType)
}

export function isPdf(mimeType: string): boolean {
  return mimeType === 'application/pdf'
}

export function isPreviewableImage(mimeType: string): boolean {
  return mimeType === 'image/jpeg' || mimeType === 'image/png'
}

/**
 * What the panel says when it cannot render the file.
 *
 * One constant because three states share it — an unsupported type, a file that
 * is corrupt or truncated, and a render that failed or timed out. A reader does
 * not care which: the useful thing to say is the same in all three, and the
 * Download button beside it still works. Splitting them would leak how the
 * viewer is built into what the reader is told.
 */
export const PREVIEW_UNAVAILABLE_MESSAGE =
  'Preview is not available for this file type. Use Download to open the file.'

/**
 * Ceiling on how long a preview may sit loading before it resolves to the
 * message above.
 *
 * A spinner with no end is the worst of the failure modes: it reads as "nearly
 * there" forever. 20s is well past a 25 MB file (the upload ceiling) on a slow
 * connection, and well short of a reader's patience.
 */
export const PREVIEW_TIMEOUT_MS = 20_000

/** Zoom stops for the image viewer. 1 is fit-to-panel, the state on open. */
export const ZOOM_STEPS: readonly number[] = [0.5, 0.75, 1, 1.5, 2, 3, 4]
export const DEFAULT_ZOOM = 1

/** Next stop up, or the current one at the top. */
export function zoomIn(zoom: number): number {
  return ZOOM_STEPS.find((step) => step > zoom) ?? ZOOM_STEPS[ZOOM_STEPS.length - 1]
}

/** Next stop down, or the current one at the bottom. */
export function zoomOut(zoom: number): number {
  const below = ZOOM_STEPS.filter((step) => step < zoom)
  return below.length > 0 ? below[below.length - 1] : ZOOM_STEPS[0]
}

export function canZoomIn(zoom: number): boolean {
  return zoom < ZOOM_STEPS[ZOOM_STEPS.length - 1]
}

export function canZoomOut(zoom: number): boolean {
  return zoom > ZOOM_STEPS[0]
}

/**
 * Keep a page number inside the document.
 *
 * Pages are 1-based, matching what the reader is shown; a document reporting no
 * pages still resolves to page 1 rather than 0, so the caller never renders
 * "Page 0 of 0".
 */
export function clampPage(page: number, totalPages: number): number {
  if (!Number.isFinite(page)) return 1
  const last = Math.max(1, Math.floor(totalPages))
  return Math.min(last, Math.max(1, Math.floor(page)))
}

/**
 * Whether to offer page navigation at all.
 *
 * A single-page document gets none — controls that cannot do anything are worse
 * than their absence, because a reader has to press one to find that out.
 */
export function hasPageNavigation(totalPages: number): boolean {
  return totalPages > 1
}

/** "Page 3 of 6", or just "1 page" when there is nowhere to navigate. */
export function pageLabel(page: number, totalPages: number): string {
  if (!hasPageNavigation(totalPages)) return '1 page'
  return `Page ${clampPage(page, totalPages)} of ${totalPages}`
}
